<template>
    <Layout :title="`Campagne starten - ${organisation.name}`">
        <div class="max-w-full h-full mx-auto py-8 px-6">
            <div class="bg-white shadow rounded-lg min-h-[80vh] flex flex-col">
                <div class="px-6 py-4 border-b border-gray-200 flex justify-center">
                    <h1 class="text-lg font-semibold text-gray-900">
                        Campagne starten voor {{ organisation.name }}
                    </h1>
                </div>
                <div class="flex justify-center">
                <div v-if="step === 1">
                        <p class="text-sm text-gray-500 mb-6 pt-3">
                            Een campagne wordt gestart voor alle deelnemers in de organisatie.
                            Gebruiker klik notificaties om een melding te krijgen van e-mail kliks.
                        </p>
                        <div class="space-y-4">
                            <div class="max-w-half">
                                <label for="attempts_per_user" class="block text-sm font-medium text-gray-700">
                                    E-mails per gebruiker*
                                </label>
                                <input type="number" id="attempts_per_user" v-model="form.attempts_per_user"
                                    :class="{ 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500': errors.attempts_per_user }"
                                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-lightblue-500 focus:border-lightblue-500 sm:text-sm"
                                    placeholder="10" />
                                <p v-if="errors.attempts_per_user" class="mt-2 text-sm text-red-600">
                                    {{ errors.attempts_per_user }}
                                </p>
                                <p v-else class="mt-2 text-sm text-gray-500">
                                    Aantal e-mail wordt verspreid over de gehele campagne duur.
                                </p>
                            </div>
                            <div class="max-w-half">
                                <label for="click_notification" class="block text-sm font-medium text-gray-700">
                                    Klik notificatie naar
                                </label>
                                <input type="text" id="click_notification" v-model="form.click_notification"
                                    :class="{ 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500': errors.click_notification }"
                                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-lightblue-500 focus:border-lightblue-500 sm:text-sm"
                                    placeholder="test@test.nl, test2@test.nl" />
                                <p v-if="errors.click_notification" class="mt-2 text-sm text-red-600">
                                    {{ errors.click_notification }}
                                </p>
                                <p v-else class="mt-2 text-sm text-gray-500">
                                    Komma gescheiden e-mail adressen.
                                </p>
                            </div>
                            <div class="max-w-half-half">
                                <label for="starting_at" class="block text-sm font-medium text-gray-700">
                                    Campagne start op*
                                </label>
                                <input type="date" id="starting_at" v-model="form.starting_at" :min="minDate"
                                    @change="handleDateValidation('starting_at')"
                                    :class="{ 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500': errors.starting_at || localCampaignErrors.starting_at }"
                                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-lightblue-500 focus:border-lightblue-500 hover:border-lightblue-400 sm:text-sm" />
                                <p v-if="localCampaignErrors.starting_at" class="mt-2 text-sm text-red-600">
                                    {{ localCampaignErrors.starting_at }}
                                </p>
                            </div>
                            <div class="max-w-half-half">
                                <label for="finished_at" class="block text-sm font-medium text-gray-700">
                                    Campagne eindigt op*
                                </label>
                                <input type="date" id="finished_at" v-model="form.finished_at"
                                    :min="form.starting_at || minDate"
                                    @change="handleDateValidation('finished_at')"
                                    :class="{ 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500': errors.finished_at || localCampaignErrors.finished_at }"
                                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-lightblue-500 focus:border-lightblue-500 hover:border-lightblue-400 sm:text-sm" />
                                <p v-if="localCampaignErrors.finished_at" class="mt-2 text-sm text-red-600">
                                    {{ localCampaignErrors.finished_at }}
                                </p>
                            </div>
                            <div>
                                <span class="block text-sm font-medium text-gray-700">Tijdsblok*</span>
                                <p class="mt-1 text-sm text-gray-500">
                                    Kies één tijdsblok waarbinnen de e-mails per dag worden verstuurd.
                                </p>
                                <div class="mt-2 grid grid-cols-2 gap-2">
                                    <div
                                        v-for="block in timeBlocks"
                                        :key="block.id"
                                        class="border flex justify-center items-center rounded-md px-3 py-4 cursor-pointer hover:border-lightblue-400"
                                        :class="{
                                            'border-lightblue-500 bg-lightblue-50': selectedTimeBlockId === block.id,
                                            'border-gray-200 bg-white': selectedTimeBlockId !== block.id
                                        }"
                                        @click="selectTimeBlock(block)"
                                    >
                                        <p class="text-sm text-gray-700 font-medium">
                                            {{ block.starting_time }} - {{ block.finished_time }}
                                        </p>
                                    </div>
                                </div>
                                <p v-if="localCampaignErrors.timeblock" class="mt-2 text-sm text-red-600">
                                    {{ localCampaignErrors.timeblock }}
                                </p>
                            </div>
                            <div>
                                <ToggleButton v-model="form.weekend_enabled" title="Weekend ingeschakeld" @input="handleWeekendToggle">
                                    <p class="text-sm text-gray-500">Vink aan om ook in het weekend e-mails te versturen</p>
                                </ToggleButton>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="p-6 flex-1 overflow-hidden">
                    <div v-if="step === 2">
                                <p class="text-sm text-gray-500 mb-4 flex justify-center">
                                    Kies per groep welke templates moeten worden verstuurd tijdens deze campagne.
                                </p>

                                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div
                                        v-for="group in groupsArray"
                                        :key="group.id"
                                        class="border border-gray-200 rounded-lg bg-white shadow-sm"
                                        :class="{
                                            'border-red-500':
                                                attemptedStep2 &&
                                                groupValidation[group.id] &&
                                                !groupValidation[group.id].valid,

                                            'border-gray-200':
                                                !attemptedStep2 ||
                                                (groupValidation[group.id] && groupValidation[group.id].valid)
                                        }"

                                    >
                                        <button
                                            type="button"
                                            @click="toggleGroup(group.id)"
                                            class="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors rounded-t-lg"
                                        >
                                            <div class="flex items-center space-x-3">
                                                <svg
                                                    class="w-5 h-5 text-gray-500 transition-transform"
                                                    :class="{ 'rotate-90': expandedGroups[group.id] }"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M9 5l7 7-7 7"
                                                    />
                                                </svg>
                                                <div>
                                                    <div class="text-sm font-medium text-gray-900">
                                                        {{ group.name }}
                                                    </div>
                                                    <div class="text-xs text-gray-500">
                                                        {{ group.attendees_count || 0 }} deelnemers
                                                    </div>
                                                </div>
                                            </div>
                                            <span class="text-xs text-gray-500">
                                                {{ getSelectedCountForGroup(group.id) }} geselecteerd
                                            </span>
                                        </button>
                                        <div
                                            v-if="attemptedStep2 && !groupValidation[group.id].valid"
                                            class="px-4 py-2 bg-red-50 text-sm text-red-700 border-t border-red-200"
                                        >
                                            Selecteer minimaal
                                            <strong>{{ groupValidation[group.id].required }}</strong>
                                            templates voor deze groep.
                                            (Nu geselecteerd:
                                            {{ groupValidation[group.id].selected }})
                                        </div>
                                        <div
                                            v-if="expandedGroups[group.id]"
                                            class="p-4 border-t border-gray-200 max-h-34/100 overflow-scroll"
                                        >
                                            <div
                                                v-for="(templateGroup, difficulty) in templates"
                                                :key="difficulty"
                                                class="mb-4"
                                            >
                                                <p
                                                    v-if="Array.isArray(templateGroup) && templateGroup.length"
                                                    class="text-xs font-medium text-gray-600 mb-2"
                                                >
                                                    Moeilijkheidsgraad {{ difficulty }}
                                                </p>
                                                <div class="grid grid-cols-1 gap-2">
                                                    <label
                                                        v-for="template in templateGroup"
                                                        :key="template.id"
                                                        class="flex items-start p-3 border rounded-md cursor-pointer hover:bg-gray-50"
                                                        :class="{
                                                            'bg-lightblue-50 border-lightblue-300':
                                                                form.group_templates[group.id] &&
                                                                form.group_templates[group.id][template.id]
                                                        }"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            v-model="form.group_templates[group.id][template.id]"
                                                            class="mt-1 mr-2 h-4 w-4 text-lightblue-600 focus:ring-lightblue-500 border-gray-300 rounded"
                                                        />
                                                        <span class="text-sm font-medium text-gray-900">
                                                            {{ template.name }}
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mt-2 bg-white px-6 py-4 flex justify-between bottom-0 z-30">
                        <button v-if="step === 2" @click="step = 1" type="button"
                            class="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50">
                            Terug
                        </button>
                        <div v-else></div>
                        <button v-if="step === 1" @click="validateAndNext" type="button" :disabled="!canProceedToStep2"
                            :class="[
                                'inline-flex justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lightblue-500',
                                canProceedToStep2
                                    ? 'bg-lightblue-600 hover:bg-lightblue-700'
                                    : 'bg-lightblue-300 cursor-not-allowed'
                            ]">
                            Volgende
                        </button>
                        <button
                            v-if="step === 2"
                            @click="startCampaign"
                            type="button"
                            class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm bg-lightblue-600 hover:bg-lightblue-700"
                        >
                            Start campagne
                        </button>
                    </div>
                </div>
    </Layout>
</template>

<script>
import Layout from '@/Shared/Layout';
import ToggleButton from '@/Shared/ToggleButton';

export default {
    components: {
        Layout,
        ToggleButton,
    },
    props: {
        organisation: {
            type: Object,
            required: true,
        },
        groups: {
            type: Array,
            default: () => [],
        },
        templates: {
            type: Object,
            required: true,
        },
        errors: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            step: 1,
            expandedGroups: {},
            localCampaignErrors: {},
            timeBlocks: [
                { id: 1, starting_time: '07:00', finished_time: '16:00' },
                { id: 2, starting_time: '07:30', finished_time: '16:30' },
                { id: 3, starting_time: '08:00', finished_time: '17:00' },
                { id: 4, starting_time: '08:30', finished_time: '17:30' },
            ],
            attemptedStep2: false,
            selectedTimeBlockId: null,
            form: {
                organisation_id: null,
                attempts_per_user: null,
                click_notification: null,
                starting_at: null,
                starting_time: null,
                finished_at: null,
                finished_time: null,
                weekend_enabled: false,
                group_templates: {},
            },
        };
    },
    computed: {
        groupsArray() {
            const groups = Array.isArray(this.groups)
                ? this.groups
                : Object.values(this.groups);

            return groups.filter(group => (group.attendees_count || 0) > 0);
        },

        groupValidation() {
            const validation = {};
            const required = Number(this.form.attempts_per_user || 0);

            this.groupsArray.forEach(group => {
                const selectedCount = Object.values(
                    this.form.group_templates[group.id] || {}
                ).filter(Boolean).length;

                validation[group.id] = {
                    selected: selectedCount,
                    required,
                    valid: selectedCount >= required,
                };
            });

            return validation;
        },

        minDate() {
            return new Date().toISOString().split('T')[0];
        },

        canProceedToStep2() {
            return (
                this.form.attempts_per_user &&
                this.form.starting_at &&
                this.form.finished_at &&
                this.selectedTimeBlockId !== null
            );
        },

        canStartCampaign() {
            return this.groupsArray.every(
                group => this.groupValidation[group.id]?.valid
            );
        },
    },
        mounted() {
            this.form.organisation_id = this.organisation.id;

            this.groupsArray.forEach(group => {
                if (!this.form.group_templates[group.id]) {
                    this.$set(this.form.group_templates, group.id, {});
                }
                this.$set(this.expandedGroups, group.id, true);
            });
        },

    methods: {
        toggleGroup(groupId) {
            this.$set(this.expandedGroups, groupId, !this.expandedGroups[groupId]);
        },
        getSelectedCountForGroup(groupId) {
            const templates = this.form.group_templates[groupId] || {};
            return Object.values(templates).filter(v => v).length;
        },
        selectTimeBlock(block) {
            this.selectedTimeBlockId = block.id;
            this.form.starting_time = block.starting_time;
            this.form.finished_time = block.finished_time;
            this.validateForm();
        },
        isWeekend(dateString) {
            if (!dateString) return false;
            const date = new Date(dateString);
            const day = date.getDay();
            return day === 0 || day === 6;
        },
        handleWeekendToggle() {
            this.validateWeekendDates();
        },
        validateWeekendDates() {
            const fields = ['starting_at', 'finished_at'];
            fields.forEach(field => {
                const dateValue = this.form[field];
                if (dateValue && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                    if (!this.form.weekend_enabled && this.isWeekend(dateValue)) {
                        this.localCampaignErrors = Object.assign({}, this.localCampaignErrors, { [field]: 'Weekend datums zijn uitgeschakeld. Selecteer een werkdag.' });
                    } else {
                        if (this.localCampaignErrors && this.localCampaignErrors[field] && this.localCampaignErrors[field].includes('Weekend datums')) {
                            const { [field]: removed, ...rest } = this.localCampaignErrors;
                            this.localCampaignErrors = rest;
                        }
                    }
                }
            });
        },
        handleDateValidation(field) {
            const dateValue = this.form[field];
            if (dateValue && /^\d{4}-\d{2}-\d{2}$/.test(dateValue) && !this.form.weekend_enabled && this.isWeekend(dateValue)) {
                this.localCampaignErrors = Object.assign({}, this.localCampaignErrors, { [field]: 'Weekend datums zijn uitgeschakeld. Selecteer een werkdag.' });
            } else if (dateValue && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                if (this.localCampaignErrors && this.localCampaignErrors[field] && this.localCampaignErrors[field].includes('Weekend datums')) {
                    const { [field]: removed, ...rest } = this.localCampaignErrors;
                    this.localCampaignErrors = rest;
                }
            }
            this.validateDateOrder();
            this.validateWeekendDates();
        },
        validateDateOrder() {
            if (this.form.starting_at && this.form.starting_time && this.form.finished_at && this.form.finished_time) {
                if (!this.isDateOrderValid()) {
                    this.localCampaignErrors = Object.assign({}, this.localCampaignErrors, { finished_at: 'Einddatum en -tijd moeten na startdatum en -tijd liggen.' });
                } else {
                    if (this.localCampaignErrors && this.localCampaignErrors.finished_at && this.localCampaignErrors.finished_at.includes('Einddatum en -tijd')) {
                        const { finished_at, ...rest } = this.localCampaignErrors;
                        this.localCampaignErrors = rest;
                    }
                }
            }
        },
        isStartInFuture() {
            try {
                if (!this.isValidYmd(this.form.starting_at) || !this.form.starting_time) {
                    return false;
                }
                const start = new Date(`${this.form.starting_at}T${this.form.starting_time}`);
                const now = new Date();
                if (isNaN(start.getTime())) {
                    return false;
                }
                return start.getTime() >= now.getTime();
            } catch (e) {
                return false;
            }
        },
        isDateOrderValid() {
            try {
                if (!this.isValidYmd(this.form.starting_at) || !this.form.starting_time) { return false; }
                if (!this.isValidYmd(this.form.finished_at) || !this.form.finished_time) { return false; }
                if (this.form.starting_at === this.form.finished_at) { return false; }
                const start = new Date(`${this.form.starting_at}T${this.form.starting_time}`);
                const end = new Date(`${this.form.finished_at}T${this.form.finished_time}`);
                if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                    return false;
                }
                return end > start;
            } catch (e) {
                return false;
            }
        },
        isValidYmd(value) {
            return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
        },
        validateForm() {
            this.localCampaignErrors = this.localCampaignErrors || {};
            if (this.selectedTimeBlockId === null) {
                this.localCampaignErrors = Object.assign({}, this.localCampaignErrors, { timeblock: 'Selecteer een tijdsblok.' });
            } else {
                if (this.localCampaignErrors && this.localCampaignErrors.timeblock) {
                    const { timeblock, ...rest } = this.localCampaignErrors;
                    this.localCampaignErrors = rest;
                }
            }
            if (this.form.starting_at && this.form.finished_at) {
                if (this.form.starting_at === this.form.finished_at) {
                    this.localCampaignErrors = Object.assign({}, this.localCampaignErrors, { finished_at: 'Einddatum moet na startdatum zijn.' });
                } else if (this.form.finished_at < this.form.starting_at) {
                    this.localCampaignErrors = Object.assign({}, this.localCampaignErrors, { finished_at: 'Einddatum moet na startdatum zijn.' });
                } else {
                    if (this.localCampaignErrors && this.localCampaignErrors.finished_at) {
                        const { finished_at, ...rest } = this.localCampaignErrors;
                        this.localCampaignErrors = rest;
                    }
                }
            }
            this.validateWeekendDates();
        },
        validateAndNext() {
            this.validateForm();
            
            if (Object.keys(this.localCampaignErrors).length > 0) {
                return;
            }
            
            if (this.canProceedToStep2) {
                this.step = 2;
            }
        },
        startCampaign() {
            this.attemptedStep2 = true;

            const hasErrors = this.groupsArray.some(
                group => !this.groupValidation[group.id].valid
            );

            if (hasErrors) {
                return;
            }

            this.$inertia.post('/campaign', {
                ...this.form,
                group_templates: this.getGroupedSelectedTemplates(),
            });

        },
        getGroupedSelectedTemplates() {
            const result = {};

            Object.entries(this.form.group_templates).forEach(([groupId, groupTemplates]) => {
                const templates = Object.keys(groupTemplates)
                    .filter(templateId => groupTemplates[templateId] === true)
                    .map(templateId => Number(templateId));

                if (templates.length > 0) {
                    result[groupId] = templates;
                }
            });

            return result;
        }
    },
};
</script>
